/** @jsxImportSource @emotion/react */
import { useTheme } from '@emotion/react';
import Box from '@mui/material/Box';
import Button from '@Button';
import NavBar from '@NavBar';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PreloadStatus from '../../Data/PreloadStatus';
import SubmissionStatus from '../../Data/SubmissionStatus';
import FEATURES from '../../Data/features';
import { isNavigationBlockedOnPage } from '../../Hooks/useNavigation/useNavigation.helpers';
import {
  setPendingMoveForwards,
  setShouldNavigateForward,
  setAttemptToNavigate,
} from '../../Lib/redux/slices/navigationControls';
import makeNavigationBarStyling from './makeNavigationBarStyling';
import useIsForwardNavigationBlockedBySupplierCard from '../../Hooks/useIsForwardNavigationBlockedBySupplierCard/useIsForwardNavigationBlockedBySupplierCard';
import { ComponentTypes } from '../../Data/Components';
import SelectAndConnect from '../../Data/SelectAndConnect';
import { filterComponentsForPage } from '../../Helpers/components';
import {
  isPhoneAndPostcodeFilled,
  isEveryComponentValid,
} from '../__helpers__/component.helpers';
import { getVisiblePageComponents } from '../Page/page.helpers';
import {
  getButtonTextForCurrentPage,
  handleClickBackward,
} from './navigationBar.helpers';
import useGetExperimentName from '../../Hooks/useGetExperimentName/useGetExperimentName';
import { shouldShowSubmissionBlockPage } from '../../Helpers/submission';

const NavigationBar = () => {
  const dispatch = useDispatch();
  const {
    currentPage,
    totalPages,
    isFinalPageBeforeSubmission,
    isFirstVisiblePostSubmissionPage,
    isLoading,
  } = useSelector((i) => i.navigation);

  const {
    pendingMoveForwards,
    blockedDestinationPage,
    pagesWithNavigationBlocks,
  } = useSelector((store) => store.navigationControls);
  const { submissionBlocks } = useSelector((store) => store.submission);
  const { pages } = useSelector((store) => store.paging);
  const {
    submissionPageNumber,
    featureFlags,
    navigationBarButtonTextConfig,
    countryCode,
    phoneNumberCountryCode,
    subcategoryId,
  } = useSelector((store) => store.formConfig);
  const { dynamicNewsletterName } = useSelector((store) => store.newsletter);
  const {
    isDynamicHeightEnabled,
    minimumWidgetHeight,
    maximumWidgetHeight,
    isConsentStatementAboveNavigation:
      isConsentStatementAboveNavigationFormConfig,
  } = useSelector((store) => store.optionalFormConfig);
  const { hasStatementGraduallyDisappeared, statement } = useSelector(
    (store) => store.legalStatement
  );
  const { preloadStatus: cpcCardPreloadStatus, showCpcRecommendations } =
    useSelector((store) => store.cpcCard);
  const { preloadStatus: appointmentBookingPreloadStatus } = useSelector(
    (store) => store.appointmentBooking
  );
  const { submissionStatus, id: submissionId } = useSelector(
    (store) => store.submission
  );
  const {
    requiresLegalStatementAboveNav:
      supplierMatchesRequireLegalStatementAboveNav,
  } = useSelector((store) => store.supplierMatches);
  const isConsentStatementAboveNavigation =
    isConsentStatementAboveNavigationFormConfig ||
    supplierMatchesRequireLegalStatementAboveNav;
  const theme = useTheme();

  const buttonExperimentName = useGetExperimentName({
    componentName: 'Button',
  });
  const navBarExperimentName = useGetExperimentName({
    componentName: 'NavBar',
  });
  const styling = makeNavigationBarStyling({
    theme,
    isSmartAdvance: featureFlags.includes(FEATURES.SMART_ADVANCE),
  });
  let { componentIdToSagaStatus } = useSelector((store) => store.dataMappings);
  const isPenultimatePage =
    typeof submissionPageNumber === 'number'
      ? currentPage === submissionPageNumber
      : currentPage + 1 === totalPages;

  const isShowingSubmissionBlockPage = shouldShowSubmissionBlockPage(
    currentPage,
    submissionBlocks
  );
  const isDestinationPageBlocked = !!(
    blockedDestinationPage &&
    isNavigationBlockedOnPage(
      blockedDestinationPage,
      pagesWithNavigationBlocks
    ) &&
    !isShowingSubmissionBlockPage
  );

  if (!componentIdToSagaStatus) {
    componentIdToSagaStatus = {};
  }

  /**
   * isAnyAsyncOperationInProgress handles when the saga is in progress and it's return and update to store will interfere with the rendering of the next page.
   */
  const [isAnyAsyncOperationInProgress, setIsAnyAsyncOperationInProgress] =
    useState(
      Object.values(componentIdToSagaStatus).some(
        (status) => status === 'PENDING'
      )
    );

  const { showAppointmentBooking } = useSelector(
    (state) => state.appointmentBooking
  );

  const components = useSelector((store) => store.components);
  const pageComponentIds = pages[currentPage];
  const componentsOnPage = filterComponentsForPage(
    components,
    pageComponentIds
  );

  const visiblePageComponents = getVisiblePageComponents(
    pageComponentIds,
    components,
    dynamicNewsletterName,
    phoneNumberCountryCode,
    subcategoryId
  );

  const isReadyToTryProceed =
    isEveryComponentValid({ components: visiblePageComponents, countryCode }) ||
    isPhoneAndPostcodeFilled(visiblePageComponents);

  const isBackButtonDisabled =
    currentPage === 0 ||
    isFirstVisiblePostSubmissionPage ||
    featureFlags.includes(FEATURES.SMART_ADVANCE);

  const isSagaInProgress = Object.values(componentIdToSagaStatus).some(
    (status) => status === 'PENDING'
  );

  const isCpcCardLoading =
    cpcCardPreloadStatus === PreloadStatus.IN_PROGRESS ||
    cpcCardPreloadStatus === PreloadStatus.WAITING_FOR_START;

  const isAppointmentBookingLoading =
    appointmentBookingPreloadStatus === PreloadStatus.IN_PROGRESS ||
    appointmentBookingPreloadStatus === PreloadStatus.WAITING_FOR_START;

  let buttonTextForCurrentPage = getButtonTextForCurrentPage({
    currentPage,
    navigationBarButtonTextConfig,
    isFinalPageBeforeSubmission,
    isPenultimatePage,
  });

  if (
    Object.values(componentsOnPage).some(
      (component) =>
        component.component.type === ComponentTypes.SELECT_AND_CONNECT
    )
  ) {
    // This can be removed when current select and connect pages have been updated to have
    // Complete Your Quote Request as the custom CTA for the page
    buttonTextForCurrentPage = SelectAndConnect.SUBMIT_BUTTON_TEXT;
  }

  const { isForwardNavigationBlockedBySupplierCard } =
    useIsForwardNavigationBlockedBySupplierCard();

  const isForwardNavigationBlocked =
    showCpcRecommendations || isForwardNavigationBlockedBySupplierCard;

  const isPreSubmissionPage = submissionId === undefined;
  const isContinueButtonHidden =
    (isForwardNavigationBlocked && isPreSubmissionPage) ||
    isShowingSubmissionBlockPage;

  useEffect(() => {
    /**
     * componentIdToSagaStatus exists to prevent a race condition, we use the status to determine if the saga is in progress and if so we prevent the user from navigating to the next page
     */
    setIsAnyAsyncOperationInProgress(isSagaInProgress);
  }, [componentIdToSagaStatus]);

  const handleClickForward = () => {
    /**
     * on click / enter press we need to wait for the async requests to resolve before navigation
     */
    if (
      isAnyAsyncOperationInProgress === true ||
      isCpcCardLoading === true ||
      isAppointmentBookingLoading === true ||
      isDestinationPageBlocked ||
      submissionStatus === SubmissionStatus.IN_PROGRESS
    ) {
      dispatch(setPendingMoveForwards({ isAwaitingAsyncRequest: true }));
    } else {
      dispatch(setShouldNavigateForward({ shouldNavigateForward: true }));
    }
  };

  const handleEnterPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      dispatch(setAttemptToNavigate({ attemptToNavigate: true }));
    }
  };

  if (showAppointmentBooking) {
    /**
     * If the appointment booking widget is visible we need to disable the navigation bar
     */
    return null;
  }

  const newsletterComponentsOnPage = Object.values(componentsOnPage).filter(
    (component) => component.component.type === 'newsletter-signup'
  );

  if (newsletterComponentsOnPage.length > 0) {
    // if current page contains newsletter components, disable the navigation bar
    return null;
  }

  return (
    <Box
      css={[styling.box]}
      onKeyDown={(event) => handleEnterPress(event)}
      data-testid="navigationBarContainer"
    >
      {isBackButtonDisabled ? (
        !isContinueButtonHidden && (
          <Button
            shouldPulse={isReadyToTryProceed}
            css={styling.button}
            label={buttonTextForCurrentPage}
            onClick={handleClickForward}
            dataTestId="continuecta"
            loading={isLoading}
            disabled={isForwardNavigationBlocked}
            experimentName={buttonExperimentName}
            textOverflowHandling={
              featureFlags.includes(FEATURES.LARGER_TEXT)
                ? 'dynamicFontSize'
                : 'none'
            }
          />
        )
      ) : (
        <NavBar
          continueButtonShouldPulse={isReadyToTryProceed}
          disabled={isBackButtonDisabled}
          buttonLabel={buttonTextForCurrentPage}
          onClickContinue={handleClickForward}
          experimentName={navBarExperimentName}
          onClickBack={() =>
            handleClickBackward({
              dispatch,
              isDynamicHeightEnabled,
              isConsentStatementAboveNavigation,
              pendingMoveForwards,
              blockedDestinationPage,
              isLoading,
              isPenultimatePage,
              hasStatementGraduallyDisappeared,
              minimumWidgetHeight,
              maximumWidgetHeight,
              statement,
            })
          }
          loading={isLoading}
          buttonDisabled={isForwardNavigationBlocked}
          textOverflowHandling={
            featureFlags.includes(FEATURES.LARGER_TEXT)
              ? 'dynamicFontSize'
              : 'none'
          }
          continueButtonHidden={isContinueButtonHidden}
        />
      )}
    </Box>
  );
};

export default NavigationBar;
